import { get, debounce, attempt } from "lodash";
import { object, ValidationError } from "yup";
import { pipe, join } from "lodash/fp";

import { batch } from "react-redux";

import {
  REGISTRATION_STEPS,
  STEPS_SEQUENCE
} from "@tvg/atomic-ui/_static/Constants/progressBar";
import usaService from "@tvg/api/usa";
import uamService from "@tvg/api/uam";
import mediator from "@tvg/mediator";

import { capitalizeFirstLetter, isTvg5 } from "@tvg/utils/generalUtils";
import { openExternalApp } from "@tvg/utils/mediatorUtils";

import tvgConf from "@tvg/conf";
import { handlePxResponse } from "@tvg/perimeterx";
import sendToAppsFlyer, { AppsFlyerEvents } from "@tvg/utils/appsflyerUtils";

import {
  cleanRegistrationAction,
  setAddressInfoScreenAction,
  setCurrentStepRegistrationAction,
  setLastStepRegistrationAction,
  setWelcomeBirthRegistrationAction,
  handleErrorModalRegistrationAction,
  setFullSsnValidFormRegistrationAction,
  setSsnFieldsRegistrationAction,
  setSsnValidFormRegistrationAction,
  handleLegalContentModalRegistrationAction,
  setLegalContentModalOriginRegistrationAction,
  setLocationAddressFieldsRegistrationAction,
  setOnfidoDetailsRegistrationAction
} from "../actions";
import { onClickGtmInfoScreenEvent } from "./gtm/infoErrors";
import {
  onClickContinueBtnGtmEvent,
  onFormStepSuccessGtmEvent,
  onRegistrationAttemptGtmEvent,
  onRegistrationFailureGtmEvent,
  onRegistrationCompleteGtmEvent
} from "./gtm/formCompletion";

import { backOff, buildUserDetails, buildUrlWelcomeFields } from "./utils";
import {
  onErrorInputGtmEvent,
  onFillValidatedInputGtmEvent
} from "./gtm/inputRelated";
import mapFieldsGtm from "./gtm/mapFieldsGtm";
import { onVirtualPageViewGtmEvent } from "./gtm/pageViews";

export const errorAge = "Not old enough";
export const errorRedirect = "Invalid state for TVG";

export const handleSelectChangeGtm = (fieldName, isEditing, module, value) => {
  if (value) {
    onFillValidatedInputGtmEvent(mapFieldsGtm[fieldName], module, isEditing);
  }
};

export const handleInputOnBlurGtm =
  (fieldName, schema, isEditing, module) => async (value) => {
    try {
      await schema.validate(value, {
        abortEarly: true
      });
      // Validation is successful
      onFillValidatedInputGtmEvent(mapFieldsGtm[fieldName], module, isEditing);
    } catch (err) {
      // Field validation failed
    }
  };

const debouncedFieldErrorGtm = debounce(
  (error, module, isEditing) => onErrorInputGtmEvent(error, module, isEditing),
  300
);

export const handleOnTheFlyChange = async (
  isFormDirty,
  fieldName,
  value,
  schema,
  formRef,
  module,
  isEditing = false
) => {
  if (isFormDirty) {
    try {
      await schema.validate(value, {
        abortEarly: true
      });
      formRef.current.setFieldError(fieldName, "");
    } catch (err) {
      if (formRef && formRef.current) {
        formRef.current.setFieldError(
          fieldName,
          get(err, "errors[0]", "Please fill this field")
        );
        debouncedFieldErrorGtm(
          get(err, "errors[0]", "Please fill this field"),
          module,
          isEditing
        );
      }
    }
  }

  // Put first letter uppercase only for firstName and lastName fields
  if (
    (fieldName === "firstName" || fieldName === "lastName") &&
    typeof value === "string"
  ) {
    const input = formRef.current.getFieldRef(fieldName);

    if (!/[A-Z]/.test(value[0])) {
      const start = input.current.selectionStart;
      const end = input.current.selectionEnd;
      const capitalizedValue = capitalizeFirstLetter(value);

      formRef.current.setFieldValue(fieldName, capitalizedValue);
      input.current.setSelectionRange(start, end);
    }
  }
};

export const handleInputOnTheFlyChange = debounce(handleOnTheFlyChange, 500);

export const stateOptions = tvgConf()
  .getStates()
  .map(({ name, abbreviation }) => ({
    key: name,
    value: abbreviation
  }));

export const cleanRegistration = (dispatch) => {
  dispatch(cleanRegistrationAction());
};

export const handleBackButtonHeader = ({
  dispatch,
  currentStep,
  offset = 0
}) => {
  const stepsOrder = STEPS_SEQUENCE;
  const currentIndex = stepsOrder.indexOf(currentStep);
  if (currentIndex > 0) {
    dispatch(
      setCurrentStepRegistrationAction(stepsOrder[currentIndex - 1 - offset])
    );
  }
};

export const getPromosInfo = ({
  stringCode,
  promoResponse,
  message,
  isRafCode
}) => {
  if (isRafCode) {
    return {
      ...message.raf,
      code: "raf",
      promoReference: stringCode,
      name:
        get(promoResponse, "PromoFriendlyName", message.default.title) ||
        message.default.title,
      instructions:
        get(
          promoResponse,
          "PreRegistrationInstructions",
          message.raf.description
        ) || message.raf.description,
      nextSteps: get(promoResponse, "PostRegistrationNextSteps"),
      TCLink: get(promoResponse, "TermsAndConditionsLink", ""),
      gtmModule: "Referral"
    };
  }

  return {
    code: get(promoResponse, "PromotionId"),
    promoReference: stringCode,
    title: get(promoResponse, "PreRegistrationTitle"),
    text: get(promoResponse, "PromotionText", ""),
    name:
      get(promoResponse, "PromoFriendlyName", message.default.title) ||
      message.default.title,
    description:
      get(promoResponse, "PublicDescription", message.default.description) ||
      message.default.description,
    instructions:
      get(
        promoResponse,
        "PreRegistrationInstructions",
        message.default.description
      ) || message.default.description,
    nextSteps: get(promoResponse, "PostRegistrationNextSteps"),
    TCLink: get(promoResponse, "TermsAndConditionsLink", ""),
    gtmModule: "Pre-Applied Promo"
  };
};

export const handleSubmitCatch = (
  isFormValid,
  validAction,
  dispatch,
  err,
  formRef,
  module,
  isEditing = false
) => {
  const validationErrors = {};
  if (err instanceof ValidationError) {
    err.inner.forEach((error) => {
      if (!validationErrors[error.path]) {
        validationErrors[error.path] = error.message;
      }
    });
    formRef.current?.setErrors(validationErrors);
    // Set invalid form in redux if not invalid yet
    if (isFormValid) {
      dispatch(validAction(false));
    }

    const concatenatedErrors = Object.values(validationErrors).reduce(
      (accumulator, error, index) => {
        if (index === 0) {
          return error;
        }
        return `${accumulator}, ${error}`;
      },
      ""
    );

    // Send GTM of inputs with errors concatenated
    onErrorInputGtmEvent(concatenatedErrors, module, isEditing);
  }
};

export const handleNativeCameraAccess = (dispatch) => (data) => {
  const isEnabled = get(data, "payload.isEnabled", false);

  if (isEnabled) {
    dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.ONFIDO));
    dispatch(setAddressInfoScreenAction({ isOpen: false }));
  } else {
    dispatch(
      handleErrorModalRegistrationAction({
        isOpen: true,
        status: "failCreateAccount"
      })
    );
  }
};

const setOnfidoStep = (dispatch) => {
  const { product } = tvgConf();

  if (window?.handleNativeMessages && product === "iosnative") {
    window.handleNativeMessages("CHECK_CAMERA_ACCESS");
  } else if (["androidwrapper", "tvgandroid"].includes(product)) {
    mediator.ios.dispatch({
      type: "CHECK_CAMERA_ACCESS"
    });
  } else {
    dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.ONFIDO));
    dispatch(setAddressInfoScreenAction({ isOpen: false }));
  }
};

export const onClickSuccess =
  ({
    dispatch,
    currentStep,
    status,
    redirectUrl,
    watchTVGUrl,
    gtmData,
    welcomeFields
  }) =>
  () => {
    if (gtmData) {
      onClickGtmInfoScreenEvent(gtmData);
    }

    if (status === "brandError" && redirectUrl) {
      const url = buildUrlWelcomeFields(redirectUrl, welcomeFields);
      if (
        tvgConf().product === "ios2" ||
        tvgConf().product === "iosnative" ||
        tvgConf().product === "androidnative"
      ) {
        openExternalApp(url);
      } else {
        window.location.assign(url);
      }
    } else if (
      status === "failCreateAccount" ||
      status === "onfidoChecksErrorWithChat"
    ) {
      if (
        window?.handleNativeMessages &&
        (tvgConf().product === "iosnative" ||
          tvgConf().product === "androidnative")
      ) {
        window.handleNativeMessages(
          "OPEN_EXTERNAL_URL",
          "https://support.tvg.com/s/"
        );
      } else {
        openExternalApp("https://support.tvg.com/s/");
      }
    } else if (status === "invalidWageringState" && watchTVGUrl) {
      window.location.assign(watchTVGUrl);
    } else if (status === "onfidoError") {
      setOnfidoStep(dispatch);
    } else {
      dispatch(setLastStepRegistrationAction(currentStep));
      // Clear DOB before sending user to the screen
      dispatch(setWelcomeBirthRegistrationAction("__ __ ____"));
      dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.WELCOME));
      dispatch(setAddressInfoScreenAction({ isOpen: false }));
    }
  };

export const onClickClose =
  ({ status, push, gtmData }) =>
  () => {
    if (gtmData) {
      onClickGtmInfoScreenEvent(gtmData);
    }

    if (status === "failCreateAccount") {
      if (tvgConf().product === "touch3" || isTvg5()) {
        push("/forgot-credentials");
      } else {
        window.location.assign("/#forgot-credentials");
      }
    } else if (tvgConf().product === "touch3" || isTvg5()) {
      push("/");
    } else {
      window.location.assign("/");
    }
  };

export const onClickSecondary =
  ({ status, dispatch, push, gtmData }) =>
  () => {
    if (gtmData) {
      onClickGtmInfoScreenEvent(gtmData);
    }

    if (status === "failCreateAccount") {
      if (tvgConf().product === "touch3" || isTvg5()) {
        push("/login");
      } else {
        window.location.assign("/#login");
      }
    } else if (status === "onfidoChecksError") {
      if (tvgConf().product === "touch3" || isTvg5()) {
        push("/");
      } else {
        window.location.assign("/");
      }
    } else {
      dispatch(setAddressInfoScreenAction({ isOpen: false }));
      // Concatenate previous values of home and details and only reset city, state and zip
      dispatch(
        setLocationAddressFieldsRegistrationAction({
          city: "",
          state: "",
          zip: ""
        })
      );
    }
  };
export const replaceAll = (pattern) => (replacement) => (text) => {
  if (text && pattern && replacement) {
    return text.replace(RegExp(pattern, "g"), replacement);
  }
  return text;
};

const getBrandName = (brand) => {
  switch (brand) {
    case "iowa":
      return "TVG - IA";
    case "ia":
      return "TVG - IA";
    case "4njbets":
      return "4NJBets";
    default:
      return "TVG";
  }
};

export const getRedirectBrand = ({ redirectUrl }) => {
  let redirectBrand = "/home";
  if (redirectUrl) {
    try {
      redirectBrand = new URL(`https://${redirectUrl}`).host;
    } catch (exception) {
      redirectBrand = "/home";
    }
  }
  if (redirectBrand.includes("www.")) {
    redirectBrand = redirectBrand.split("www.").pop();
  }
  if (redirectBrand.includes(".")) {
    redirectBrand = redirectBrand.split(".")[0];
  }
  if (redirectBrand.includes("-")) {
    redirectBrand = redirectBrand.split("-")[0];
  }
  return getBrandName(redirectBrand);
};

export const replaceBrandBy = replaceAll("@@@brand@@@");
export const replaceCurrentBrandBy = replaceAll("@@@currentBrand@@@");
export const replaceStateNameBy = replaceAll("@@@statename@@@");
export const replaceAgeBy = replaceAll("@@@age@@@");

export const getBodyContent = ({ infoScreen, bodyContentField }) => {
  const redirectBrand = getRedirectBrand({
    redirectUrl: infoScreen.redirectUrl
  });
  const currentBrand = getBrandName(tvgConf().brand);

  const replaceLabels = pipe(
    replaceBrandBy(redirectBrand),
    replaceCurrentBrandBy(currentBrand),
    replaceStateNameBy(infoScreen.state),
    replaceAgeBy(infoScreen.ageLimit)
  );

  const replaceLabelsText = pipe(
    replaceBrandBy(redirectBrand),
    replaceCurrentBrandBy(redirectBrand)
  );

  return bodyContentField.map(({ title, text, ...rest }) => ({
    title: replaceLabels(title),
    text: replaceLabelsText(text),
    ...rest
  }));
};

export const handleLogin = (loginData, accountId, selectedPromo) => {
  return backOff(function backOffHandleLogin(next, stop) {
    return usaService
      .tryUserLogin(loginData)
      .then((resp) => {
        onRegistrationCompleteGtmEvent(
          loginData.verified,
          accountId,
          true,
          loginData.state,
          get(selectedPromo, "promoReference")
        );

        const { product } = tvgConf();

        // Send AppsFlyer successful registration event for iOS app
        if (product === "ios2") {
          sendToAppsFlyer({
            key: AppsFlyerEvents.AfCompleteRegistration,
            values: {
              accountId: accountId.toString()
            }
          });
        }

        if (product === "iosnative" || product === "androidnative") {
          const body = {
            tvg3token: get(resp, "data.session.tvg3token", ""),
            tvgSessionId: get(resp, "data.session.tvg-session-id", ""),
            accountNumber: get(resp, "data.userDetails.accountNumber", "")
          };
          // AppsFlyer successful registration handle on react native side so we use the correct sdk
          window.handleNativeMessages("VALID_USER_SESSION", body);
        }

        if (isTvg5()) {
          mediator.base.dispatch({ type: "TVG_LOGIN:VALIDATE_SESSION" });
        }

        stop();
      })
      .catch(() => {
        next();
      });
  }, 30000);
};

export const handleRegistrationResponse =
  ({ welcomeFields, addressFields, selectedPromo }) =>
  (data) => {
    const registerData = data.data;

    if ((registerData && registerData.verified, registerData.enabled)) {
      return handleLogin(
        {
          username: get(welcomeFields, "email", ""),
          password: get(welcomeFields, "password", ""),
          state: get(addressFields, "home.state", ""),
          enabled: registerData.enabled,
          verified: registerData.verified,
          group: registerData.group
        },
        registerData.accountId,
        selectedPromo
      );
    }

    if (registerData && !registerData.verified) {
      onRegistrationCompleteGtmEvent(
        registerData.verified,
        registerData.accountId,
        false,
        get(addressFields, "home.state"),
        get(selectedPromo, "promoReference")
      );
    }
    return Promise.reject(data);
  };

export const perimeterXHandling = (
  response,

  setIsHumanChallengeEnabled
) => {
  // Has PerimeterX response
  if (
    response &&
    response.status === 403 &&
    get(response, "data.blockScript", "") !== ""
  ) {
    setIsHumanChallengeEnabled(true);
    const variables = attempt(() =>
      JSON.parse(get(response, "config.data", "{}"))
    );
    handlePxResponse(get(response, "data", {}), get(variables, "account"));

    return true;
  }
  return false;
};

export const handleSSNResponse =
  ({
    welcomeFields,
    addressFields,
    ssnFields,
    selectedPromo,
    selectedTvProvider,
    setIsHumanChallengeEnabled,
    enableRegistrationPerimeterX,
    enableOnfido
  }) =>
  (ssnResponse) => {
    const firstFiveDigits = get(ssnResponse, "data.firstFiveSSN", "");
    let response = Promise.resolve();
    if (firstFiveDigits || join("", get(ssnFields, "ssn", [])).length === 9) {
      const userDetails = buildUserDetails({
        welcomeFields,
        addressFields,
        ssnFields,
        selectedPromo,
        firstFiveDigits,
        selectedTvProvider
      });

      const registrationFields = {
        welcomeFields,
        addressFields,
        selectedPromo
      };

      response = uamService
        .postUserRegistration({ ...userDetails, enableOnfido })
        .then(handleRegistrationResponse(registrationFields))
        .catch(
          ({ response: userPostErrorResponse, data: userPostDataResponse }) => {
            onRegistrationFailureGtmEvent(
              get(userPostErrorResponse, "data.code", 200)
            );
            return Promise.reject({
              hasPerimeterX: enableRegistrationPerimeterX
                ? perimeterXHandling(
                    userPostErrorResponse,
                    setIsHumanChallengeEnabled
                  )
                : false,
              onfido: get(userPostDataResponse, "onfido", {}),
              accountId: get(userPostDataResponse, "accountId", "")
            });
          }
        );
    } else {
      response = Promise.resolve({ goToSSN: true });
    }
    return response;
  };

export const handleSubmitRegistrationButton =
  ({
    registrationTexts,
    welcomeFields,
    addressFields,
    ssnFields,
    setIsLoading,
    selectedPromo,
    selectedTvProvider,
    setIsHumanChallengeEnabled,
    enableRegistrationPerimeterX,
    enableOnfido,
    dispatch
  }) =>
  () => {
    const [birthMonth, birthDay, birthYear] = get(
      welcomeFields,
      "birth",
      ""
    ).split(" ");
    const ssnObject = {
      firstName: get(welcomeFields, "firstName", ""),
      lastName: get(welcomeFields, "lastName", ""),
      yearOfBirth: +birthYear,
      monthOfBirth: +birthMonth,
      dayOfBirth: +birthDay,
      address: addressFields.home.street,
      zipCode: addressFields.home.zip,
      lastFourSSN: join("", ssnFields.ssn)
    };
    setIsLoading(true);
    onRegistrationAttemptGtmEvent(registrationTexts.review.innerTitle);

    return uamService
      .postGetSSNValidation({ ssnObject })
      .then(
        handleSSNResponse({
          welcomeFields,
          addressFields,
          ssnFields,
          selectedPromo,
          selectedTvProvider,
          setIsHumanChallengeEnabled,
          enableRegistrationPerimeterX,
          enableOnfido
        })
      )
      .then((response) => {
        setIsLoading(false);
        if (response?.goToSSN) {
          mediator.base.dispatch({
            type: "TVG_LOGIN:VALIDATE_SESSION"
          });
          dispatch(
            setCurrentStepRegistrationAction(REGISTRATION_STEPS.FULLSSN)
          );
        } else {
          // Sets promocode for GTM page load on a page supporting inbox to trigger blaze event
          if (get(selectedPromo, "code")) {
            localStorage.setItem("promoCode", selectedPromo.promoReference);
          }
          dispatch(
            setCurrentStepRegistrationAction(REGISTRATION_STEPS.SUCCESS)
          );
          mediator.base.dispatch({
            type: "TVG_LOGIN:VALIDATE_SESSION"
          });
        }
      })
      .catch((error) => {
        if (!get(error, "hasPerimeterX", false)) {
          if (
            Object.keys(get(error, "onfido", {})).length > 0 &&
            get(error, "accountId")
          ) {
            batch(() => {
              dispatch(
                setCurrentStepRegistrationAction(REGISTRATION_STEPS.ONFIDO)
              );
              // if exists an account / have a duplicate email will get this
              dispatch(
                setOnfidoDetailsRegistrationAction({
                  accountId: error.accountId,
                  applicantId: error.onfido.applicantId,
                  sdkToken: error.onfido.sdkToken,
                  uuid: error.onfido.uuid
                })
              );
              dispatch(
                // if there is any issue with onfido validation from aristotle Will get this error
                handleErrorModalRegistrationAction({
                  isOpen: true,
                  status: "onfidoError"
                })
              );
            });
          } else {
            dispatch(
              handleErrorModalRegistrationAction({
                isOpen: true,
                status: "failCreateAccount"
              })
            );
          }
        }

        setIsLoading(false);
      });
  };

export const handleFullSSNValidation =
  ({
    fullSsnTexts,
    welcomeFields,
    addressFields,
    selectedPromo,
    setIsLoading,
    setIsFormDirty,
    formRef,
    dispatch,
    isFormValid,
    ssnSchema,
    selectedTvProvider,
    setIsHumanChallengeEnabled,
    enableRegistrationPerimeterX,
    enableOnfido
  }) =>
  async (ssnFields) => {
    onClickContinueBtnGtmEvent(fullSsnTexts.innerTitle);
    setIsLoading(true);
    setIsFormDirty(true);

    try {
      const schema = object().shape({
        ssn: ssnSchema
      });

      await schema.validate(ssnFields, {
        abortEarly: false
      });

      handleSSNResponse({
        addressFields,
        welcomeFields,
        selectedPromo,
        ssnFields,
        selectedTvProvider,
        setIsHumanChallengeEnabled,
        enableRegistrationPerimeterX,
        enableOnfido
      })()
        .then(() => {
          // Sets promocode for GTM page load on a page supporting inbox to trigger blaze event
          if (get(selectedPromo, "code")) {
            localStorage.setItem("promoCode", selectedPromo.promoReference);
          }
          dispatch(
            setCurrentStepRegistrationAction(REGISTRATION_STEPS.SUCCESS)
          );
          onFormStepSuccessGtmEvent(fullSsnTexts.innerTitle);
          setIsFormDirty(false);
          setIsLoading(false);
        })
        .catch((error) => {
          if (!get(error, "hasPerimeterX", false)) {
            // Handle onfido error to validate user information through onfido sdk
            if (
              Object.keys(error.onfido || {}).length > 0 &&
              get(error, "accountId")
            ) {
              batch(() => {
                dispatch(
                  setCurrentStepRegistrationAction(REGISTRATION_STEPS.ONFIDO)
                );
                dispatch(
                  setOnfidoDetailsRegistrationAction({
                    accountId: error.accountId,
                    applicantId: error.onfido.applicantId,
                    sdkToken: error.onfido.sdkToken,
                    uuid: error.onfido.uuid
                  })
                );
                dispatch(
                  handleErrorModalRegistrationAction({
                    isOpen: true,
                    status: "onfidoError"
                  })
                );
              });
            } else {
              // Our default error of something went wrong
              dispatch(
                handleErrorModalRegistrationAction({
                  isOpen: true,
                  status: "failCreateAccount"
                })
              );
            }
          }

          setIsLoading(false);
        });

      // Remove all previous errors
      formRef.current?.setErrors({});
      dispatch(setFullSsnValidFormRegistrationAction(true));
    } catch (err) {
      handleSubmitCatch(
        isFormValid,
        setFullSsnValidFormRegistrationAction,
        dispatch,
        err,
        formRef
      );
      setIsLoading(false);
    }
  };

export const handleShortSSNValidation =
  ({
    dispatch,
    formRef,
    isFormValid,
    setIsFormDirty,
    ssnSchema,
    isEditing,
    ssnTexts
  }) =>
  async (ssnFields) => {
    setIsFormDirty(true);
    onClickContinueBtnGtmEvent(
      `${isEditing ? "Edit " : ""}${ssnTexts.innerTitle}`
    );
    try {
      const schema = object().shape({
        ssn: ssnSchema
      });

      await schema.validate(ssnFields, {
        abortEarly: false
      });
      // Remove all previous errors
      formRef.current?.setErrors({});

      // Validation passed
      setIsFormDirty(false);
      dispatch(setSsnFieldsRegistrationAction(ssnFields));
      dispatch(setSsnValidFormRegistrationAction(true));
      dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.REVIEW));
      onFormStepSuccessGtmEvent(
        `${isEditing ? "Edit " : ""}${ssnTexts.innerTitle}`
      );
    } catch (err) {
      handleSubmitCatch(
        isFormValid,
        setSsnValidFormRegistrationAction,
        dispatch,
        err,
        formRef,
        ssnTexts.innerTitle,
        isEditing
      );
    }
  };

export const handleLegalContentButton =
  (dispatch, location) =>
  ({ isOpen, contentType }) => {
    const view =
      contentType === "termsAndConditions"
        ? "terms-and-conditions-details"
        : "privacy-policy-details";

    onVirtualPageViewGtmEvent(location, view);
    dispatch(
      handleLegalContentModalRegistrationAction({ isOpen, contentType })
    );
  };

export const handleLegalContentLinks = (event, url) => {
  event.preventDefault();
  event.stopPropagation();

  openExternalApp(url);
};

export const setLegalContentOrigin = (dispatch) => (modalOrigin) =>
  dispatch(setLegalContentModalOriginRegistrationAction(modalOrigin));

export const onClickCustomerServiceButton = () => {
  if (
    window?.handleNativeMessages &&
    (tvgConf().product === "iosnative" || tvgConf().product === "androidnative")
  ) {
    window.handleNativeMessages(
      "OPEN_EXTERNAL_URL",
      "https://support.tvg.com/s/"
    );
  } else {
    openExternalApp("https://support.tvg.com/s/");
  }
};

export const onfidoPollerCallback = ({
  poller,
  dispatch,
  onfidoFailStatus,
  accountId,
  checkId,
  welcomeFields,
  addressFields,
  selectedPromo
}) => {
  return uamService
    .getOnfidoCheck({
      accountId,
      checkId
    })
    .then(({ data }) => {
      const onfidoCheckStatus = get(data, "onfidoCheckStatus", "IN_PROGRESS");

      if (onfidoCheckStatus === "GO_MANUAL") {
        dispatch(
          handleErrorModalRegistrationAction({
            isOpen: true,
            status: onfidoFailStatus
          })
        );

        if (poller.isRunning()) {
          poller.stop();
        }
      }

      if (onfidoCheckStatus === "SUCCESS") {
        handleLogin(
          {
            username: get(welcomeFields, "email", ""),
            password: get(welcomeFields, "password", ""),
            state: get(addressFields, "home.state", ""),
            enabled: true,
            verified: true
          },
          +accountId,
          selectedPromo
        )
          .then(() => {
            dispatch(
              setCurrentStepRegistrationAction(REGISTRATION_STEPS.SUCCESS)
            );
            dispatch(
              handleErrorModalRegistrationAction({
                isOpen: false,
                status: ""
              })
            );
          })
          .catch(() => {
            dispatch(
              handleErrorModalRegistrationAction({
                isOpen: true,
                status: "loginFailure"
              })
            );

            onRegistrationCompleteGtmEvent(
              false,
              +accountId,
              false,
              get(addressFields, "home.state", ""),
              get(selectedPromo, "promoReference")
            );
          });

        if (poller.isRunning()) {
          poller.stop();
        }
      }
    })
    .catch(() => {
      dispatch(
        handleErrorModalRegistrationAction({
          isOpen: true,
          status: onfidoFailStatus
        })
      );

      if (poller.isRunning()) {
        poller.stop();
      }
    });
};
